import { APP_BASE_HREF, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient } from '@angular/common/http';
import localeEn from '@angular/common/locales/en';
import localeFr from '@angular/common/locales/fr';
import localeTr from '@angular/common/locales/tr';
import { LOCALE_ID, importProvidersFrom } from '@angular/core';
import { getAnalytics, provideAnalytics } from '@angular/fire/analytics';
import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, indexedDBLocalPersistence, initializeAuth, provideAuth } from '@angular/fire/auth';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { RouteReuseStrategy, provideRouter } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular/standalone';
import { defineCustomElements } from '@ionic/pwa-elements/loader';
import { IonicStorageModule } from '@ionic/storage-angular';
import { TranslateLoader, TranslateModule, provideTranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AppComponent } from './app/app.component';
import { AuthInterceptor } from './app/interceptors/auth.interceptor';
import { routes } from './app/routes';
import { PlatformUtils } from './app/utils/platform.utils';
import { environment } from './environments/environment';

defineCustomElements(window);
if (environment.production) {
    console.log = () => {};
}

export function createTranslateLoader(http: HttpClient) {
    // Bundles are hashed; these JSON files are not. The release version forces a reload of fr/en/tr.
    return new TranslateHttpLoader(http, './assets/i18n/', `.json?v=${environment.version}`);
}

registerLocaleData(localeFr);
registerLocaleData(localeEn);
registerLocaleData(localeTr);

PlatformUtils.setClass();

bootstrapApplication(AppComponent, {
    providers: [
        { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
        provideIonicAngular({ mode: 'ios', loadingSpinner: 'crescent' }),
        provideRouter(routes),
        provideFirebaseApp(() => initializeApp(environment.firebase)),
        provideAnalytics(() => getAnalytics()),
        provideAuth(() => {
            if (Capacitor.isNativePlatform()) {
                return initializeAuth(getApp(), {
                    persistence: indexedDBLocalPersistence,
                });
            } else {
                return getAuth();
            }
        }),
        provideAnimations(),
        provideHttpClient(),
        provideTranslateService({
            loader: {
                provide: TranslateLoader,
                useFactory: createTranslateLoader,
                deps: [HttpClient],
            },
        }),
        importProvidersFrom(
            IonicStorageModule.forRoot(),
            BrowserModule,
            TranslateModule.forRoot({
                loader: {
                    provide: TranslateLoader,
                    useFactory: createTranslateLoader,
                    deps: [HttpClient],
                },
            }),
        ),

        { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
        // ScreenTrackingService and UserTrackingService are initialized conditionally
        // in AnalyticsInitService based on cookie consent
        // Define locales
        { provide: LOCALE_ID, useValue: 'tr' },
        {
            provide: APP_BASE_HREF,
            useValue: (window as unknown as { [key: string]: string })['base-href'],
        },
        { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    ],
});
